<template>
  <!-- Teleport + z-index próprio: o visualizador é aberto de dentro do modal do
       chamado, e sem isso ele nasce como modal aninhado — ficava atrás do
       backdrop do primeiro e dava a impressão de não abrir. -->
  <Teleport to="body">
  <BModal
    v-model="showLocal"
    size="xl"
    hide-header
    hide-footer
    centered
    body-class="p-0 bg-transparent"
    class="v-modal-custom"
    modal-class="visualizador-anexo-modal"
    backdrop-class="visualizador-anexo-backdrop"
    :teleport-disabled="true"
    @hidden="fechar"
  >
    <div class="visualizador">
      <!-- Ações flutuam sobre o conteúdo em vez de ocupar uma barra: o que
           interessa aqui é o anexo, não a moldura. -->
      <div class="visualizador-acoes">
        <button
          v-if="permiteBaixar"
          type="button"
          class="visualizador-acao"
          title="Baixar"
          @click.stop="$emit('baixar')"
        >
          <i class="ri-download-2-line"></i>
        </button>
        <button
          v-if="url && tipo !== 'download'"
          type="button"
          class="visualizador-acao"
          title="Abrir em nova aba"
          @click.stop="abrirEmNovaAba"
        >
          <i class="ri-external-link-line"></i>
        </button>
        <button type="button" class="visualizador-acao" title="Fechar" @click.stop="fechar">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <div v-if="carregando" class="visualizador-aviso">
        <BSpinner variant="light" />
        <span>Abrindo o anexo...</span>
      </div>

      <img
        v-else-if="tipo === 'imagem' && url"
        :src="url"
        :alt="nome || 'Anexo'"
        class="visualizador-imagem"
        @click="fechar"
      />

      <!-- PDF e texto não fecham no clique: o usuário precisa rolar e
           selecionar dentro do documento. -->
      <iframe
        v-else-if="tipo === 'incorporado' && url"
        :src="url"
        class="visualizador-frame"
        :title="nome || 'Anexo'"
      ></iframe>

      <div v-else class="visualizador-aviso visualizador-aviso--bloco">
        <i class="ri-file-download-line"></i>
        <strong>{{ nome || "Anexo" }}</strong>
        <p class="mb-0">
          Este formato não abre no navegador. Arquivos do Office exigem o programa instalado, e o
          anexo fica em rota autenticada — não pode ser enviado a um serviço on-line sem expor o
          conteúdo do chamado. Use o botão de download.
        </p>
      </div>
    </div>
  </BModal>
  </Teleport>
</template>

<script>
/**
 * Visualizador de anexo, compartilhado entre o bloco de anexos e as imagens
 * inline da conversa do chamado.
 *
 * Segue o formato de lightbox que já existia na conversa — sem cabeçalho, fundo
 * transparente e clique na imagem para fechar. As ações ficam sobrepostas para
 * não roubar espaço do conteúdo.
 *
 * É um componente de apresentação: quem abre resolve a origem (blob
 * autenticado, URL inline) e informa o `tipo`.
 */
export default {
  name: "VisualizadorAnexo",
  props: {
    visible: { type: Boolean, default: false },
    nome: { type: String, default: "" },
    url: { type: String, default: "" },
    // 'imagem' renderiza <img>; 'incorporado' usa <iframe> (PDF, texto);
    // 'download' mostra o aviso de formato não exibível.
    tipo: { type: String, default: "imagem" },
    carregando: { type: Boolean, default: false },
    permiteBaixar: { type: Boolean, default: true },
  },
  // `update:visible` permite `v-model:visible` no pai. É o que garante que a
  // flag volte a `false` ao fechar — se ela ficar `true`, o próximo clique não
  // muda `visible`, o watch não dispara e o modal nunca mais reabre.
  emits: ["close", "baixar", "update:visible"],
  data() {
    return { showLocal: false };
  },
  watch: {
    visible: {
      immediate: true,
      handler(valor) {
        this.showLocal = Boolean(valor);
      },
    },
  },
  methods: {
    fechar() {
      this.showLocal = false;
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    abrirEmNovaAba() {
      if (!this.url) return;
      window.open(this.url, "_blank", "noopener");
    },
  },
};
</script>

<style scoped>
.visualizador {
  position: relative;
}

.visualizador-acoes {
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  z-index: 2;
  display: flex;
  gap: 0.35rem;
}

.visualizador-acao {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.1rem;
  height: 2.1rem;
  border: 0;
  border-radius: 0.55rem;
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
  font-size: 1.05rem;
  backdrop-filter: blur(4px);
  transition: background 0.15s ease;
}

.visualizador-acao:hover,
.visualizador-acao:focus-visible {
  background: rgba(15, 23, 42, 0.82);
}

.visualizador-imagem {
  display: block;
  max-width: 100%;
  max-height: 85vh;
  margin: 0 auto;
  border-radius: 0.5rem;
  cursor: zoom-out;
}

.visualizador-frame {
  display: block;
  width: 100%;
  height: 85vh;
  border: 0;
  border-radius: 0.5rem;
  background: #fff;
}

.visualizador-aviso {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 40vh;
  padding: 2rem;
  color: #fff;
}

.visualizador-aviso--bloco {
  flex-direction: column;
  gap: 0.5rem;
  max-width: 34rem;
  margin: 0 auto;
  border-radius: 0.75rem;
  background: rgba(15, 23, 42, 0.82);
  text-align: center;
  backdrop-filter: blur(4px);
}

.visualizador-aviso--bloco i {
  font-size: 2.5rem;
  opacity: 0.85;
}

.visualizador-aviso--bloco p {
  font-size: 0.86rem;
  opacity: 0.82;
}
</style>

<style>
/* Acima do modal do chamado (que já usa z-index elevado): o visualizador é
   sempre aberto de dentro dele. */
.visualizador-anexo-modal.modal {
  z-index: 1085 !important;
}

.visualizador-anexo-modal .modal-dialog,
.visualizador-anexo-modal .modal-content {
  z-index: 1085 !important;
}

.visualizador-anexo-modal .modal-content {
  background: transparent;
  border: 0;
  box-shadow: none;
}

.visualizador-anexo-backdrop.modal-backdrop {
  z-index: 1080 !important;
  opacity: 0.75;
}
</style>
